import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  getPlans,
  getPackages,
  createCheckout,
  createDynamicSubscriptionCheckout,
  getCustomerAgents,
  getCurrentSubscription,
  getPaymentStatus,
  getPaymentOrder,
  createCustomerAgentStripeBillingPortalSession,
  getApiError,
  pickStripeBillingPortalUrl,
} from '../lib/api';
import { getPublicAppBaseUrl } from '../lib/apiBaseUrl';
import { getAgentPlanName } from '../lib/subscriptionQuota';
import { toast } from 'sonner';
import { InteractivePricingCalculator } from '../components/InteractivePricingCalculator';
import {
  Check,
  Clock,
  Star,
  Loader2,
  CreditCard,
  Package,
  Phone,
  ArrowRight,
  ArrowLeft,
  Shield,
  HeartPulse,
  Sparkles,
  ListChecks,
  Globe2,
} from 'lucide-react';

/**
 * GET /subscriptions/current: always 200 (no 404 for “no subscription”).
 * Empty state: has_active_subscription: false, subscription: null, subscriptions: [].
 */
function deriveActiveSubscription(payload) {
  if (!payload) return false;
  if (typeof payload.has_active_subscription === 'boolean') {
    return payload.has_active_subscription;
  }
  if (payload.subscription && typeof payload.subscription === 'object') {
    const status = String(
      payload.subscription.status ?? payload.subscription.subscription_status ?? ''
    ).toLowerCase();
    if (['active', 'trialing'].includes(status)) return true;
  }
  if (Array.isArray(payload.subscriptions) && payload.subscriptions.length > 0) {
    return payload.subscriptions.some((s) =>
      ['active', 'trialing'].includes(String(s?.status ?? '').toLowerCase())
    );
  }
  return false;
}

/** Required for minute packs: fulfillment attaches to this agent (must have an active subscription). */
function MinutePackAgentField({ agents, selectedAgentId, onAgentChange, isAuthenticated }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 mb-8 max-w-4xl mx-auto">
      <label htmlFor="package-agent" className="block text-sm font-medium text-slate-700 mb-2">
        Select agent (required)
      </label>
      <p className="text-xs text-slate-500 mb-2">
        Minute packs are applied to the agent you choose. That agent must have an active subscription.
      </p>
      <select
        id="package-agent"
        className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
        value={selectedAgentId}
        onChange={(e) => onAgentChange(e.target.value)}
        disabled={!isAuthenticated || agents.length === 0}
        data-testid="package-agent-select"
      >
        {agents.length === 0 ? (
          <option value="">
            {isAuthenticated ? 'No agents available. Create an agent first.' : 'Sign in to load your agents'}
          </option>
        ) : (
          agents.map((agent) => (
            <option key={agent.agent_id} value={agent.agent_id}>
              {agent.name || agent.agent_id}
            </option>
          ))
        )}
      </select>
      {isAuthenticated && agents.length === 0 && (
        <p className="text-sm text-amber-800 mt-2">Create an agent from the dashboard before buying a minute pack.</p>
      )}
    </div>
  );
}

function MinutePackagesSection({
  packages,
  purchasing,
  onPurchase,
  purchaseDisabled = false,
  showPerMinuteCost = true,
}) {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <Badge className="mb-4 bg-teal-100 text-teal-700">
          <Shield className="w-4 h-4 mr-2" />
          Valid for 365 days
        </Badge>
        <p className="text-slate-600 max-w-lg mx-auto">
          Purchase additional minutes that never expire for a full year. Perfect for handling seasonal spikes or growing
          demand.
        </p>
      </div>

      <div className="grid sm:grid-cols-3 gap-8">
        {packages.map((pkg) => (
          <Card
            key={pkg.package_id}
            className="border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300"
            data-testid={`package-card-${pkg.package_id}`}
          >
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-10 h-10 text-teal-600" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{pkg.minutes} Minutes</h3>
              <p className="text-sm text-slate-500 mb-6">{pkg.name}</p>
              <div className="mb-6">
                <span className="text-4xl font-bold text-slate-900">${pkg.price}</span>
                <span className="text-sm text-slate-500 ml-1">AUD</span>
              </div>
              {showPerMinuteCost && (
                <p className="text-sm text-slate-500 mb-6">${pkg.price_per_minute} per minute</p>
              )}
              <Button
                className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                variant="default"
                onClick={() => onPurchase('package', pkg.package_id)}
                disabled={purchasing === pkg.package_id || purchaseDisabled}
                data-testid={`buy-package-btn-${pkg.package_id}`}
              >
                {purchasing === pkg.package_id ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Buy Now'
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function SubscriptionPlansSection({ plans, purchasing, onPurchase, purchaseDisabled = false }) {
  return (
    <div className="max-w-4xl mx-auto">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {plans.map((plan) => {
          const isYearly = String(plan.billing_cycle || '').toLowerCase() === 'yearly';
          const quota = plan.message_count ?? plan.minutes_per_month ?? 0;

          return (
            <Card
              key={plan.plan_id}
              className="border border-slate-200 hover:border-teal-300 hover:shadow-xl transition-all duration-300"
              data-testid={`plan-card-${plan.plan_id}`}
            >
              <CardContent className="p-8 text-center">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 flex items-center justify-center mx-auto mb-6">
                  <CreditCard className="w-10 h-10 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                <p className="text-sm text-slate-500 mb-6">{plan.description}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-slate-900">${plan.price}</span>
                  <span className="text-sm text-slate-500 ml-1">AUD / {isYearly ? 'year' : 'month'}</span>
                </div>
                <p className="text-sm text-slate-500 mb-2">{quota} messages/month</p>
                {isYearly && Number(plan.yearly_savings || 0) > 0 && (
                  <p className="text-sm text-green-600 mb-6">Save ${plan.yearly_savings} yearly</p>
                )}
                {!isYearly && <div className="mb-6" />}
                <Button
                  className="w-full h-12 bg-slate-900 hover:bg-slate-800"
                  variant="default"
                  onClick={() => onPurchase('subscription', plan.plan_id)}
                  disabled={purchasing === plan.plan_id || purchaseDisabled}
                  data-testid={`subscribe-btn-${plan.plan_id}`}
                >
                  {purchasing === plan.plan_id ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export function PricingPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isBuyMinutes = location.pathname === '/buy-minutes';
  /** `/pricing` loads agents for calculator checkout; `/buy-minutes` loads catalog + agents. */
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [purchasing, setPurchasing] = useState(null);
  const [calculatorCheckoutLoading, setCalculatorCheckoutLoading] = useState(false);
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const navigate = useNavigate();
  const isAuthenticated = !!localStorage.getItem('token');
  const requestedAgentId = searchParams.get('agentId');
  const requestedTab = searchParams.get('tab');

  useEffect(() => {
    if (requestedTab === 'plans' || requestedTab === 'packages') {
      setActiveTab(requestedTab);
    }
  }, [requestedTab]);

  useEffect(() => {
    if (!isBuyMinutes) return;
    if (hasActiveSubscription) return;
    if (requestedTab === 'plans' || requestedTab === 'packages') return;
    setActiveTab('packages');
  }, [isBuyMinutes, hasActiveSubscription, requestedTab]);

  const loadData = useCallback(async () => {
    if (!isBuyMinutes) {
      try {
        if (isAuthenticated) {
          const agentsRes = await getCustomerAgents({ limit: 200 }).catch(() => ({ data: { agents: [] } }));
          const loadedAgents = Array.isArray(agentsRes?.data?.agents) ? agentsRes.data.agents : [];
          setAgents(loadedAgents);
          if (loadedAgents.length > 0) {
            const hasRequestedAgent =
              requestedAgentId && loadedAgents.some((agent) => agent.agent_id === requestedAgentId);
            setSelectedAgentId((prev) => {
              if (hasRequestedAgent) return requestedAgentId;
              return prev || loadedAgents[0].agent_id;
            });
          }
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const [plansRes, packagesRes, agentsRes, subscriptionRes] = await Promise.all([
        getPlans(),
        getPackages(),
        isAuthenticated
          ? getCustomerAgents({ limit: 200 }).catch(() => ({ data: { agents: [] } }))
          : Promise.resolve({ data: { agents: [] } }),
        isAuthenticated
          ? getCurrentSubscription().catch(() => ({ data: null }))
          : Promise.resolve({ data: null }),
      ]);
      setPlans(plansRes.data.plans);
      setPackages(packagesRes.data.packages);
      setHasActiveSubscription(deriveActiveSubscription(subscriptionRes?.data));
      const loadedAgents = Array.isArray(agentsRes?.data?.agents) ? agentsRes.data.agents : [];
      setAgents(loadedAgents);
      if (loadedAgents.length > 0) {
        const hasRequestedAgent = requestedAgentId && loadedAgents.some((agent) => agent.agent_id === requestedAgentId);
        setSelectedAgentId((prev) => {
          if (hasRequestedAgent) return requestedAgentId;
          return prev || loadedAgents[0].agent_id;
        });
      }
    } catch (error) {
      toast.error('Failed to load pricing');
    } finally {
      setLoading(false);
    }
  }, [isBuyMinutes, isAuthenticated, requestedAgentId]);

  const selectedAgentForBilling = agents.find((a) => a.agent_id === selectedAgentId);
  const showCustomerBillingPortal =
    isAuthenticated && selectedAgentId && Boolean(getAgentPlanName(selectedAgentForBilling));

  const handleCustomerBillingPortal = async () => {
    if (!selectedAgentId) {
      toast.error('Select an agent first.');
      return;
    }
    setBillingPortalLoading(true);
    try {
      const base = getPublicAppBaseUrl();
      const pathPrefix = isBuyMinutes ? '/buy-minutes' : '/pricing';
      const returnUrl = `${base}${pathPrefix}${typeof window !== 'undefined' ? window.location.search : ''}`;
      const res = await createCustomerAgentStripeBillingPortalSession(selectedAgentId, {
        return_url: returnUrl,
        send_email: false,
      });
      const portalUrl = pickStripeBillingPortalUrl(res?.data);
      if (!portalUrl) {
        toast.error('Billing portal URL was not returned by the server');
        return;
      }
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
      toast.success('Billing portal opened in a new tab.');
    } catch (error) {
      const n = getApiError(error);
      toast.error(n.detail || n.uiMessage || 'Could not open billing portal');
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const handleCalculatorSubscribe = async ({ calls, sms, facebookInteractions, industryId }) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to subscribe');
      navigate('/login');
      return;
    }
    if (!selectedAgentId || agents.length === 0) {
      toast.error('Select an agent before subscribing.');
      return;
    }
    setCalculatorCheckoutLoading(true);
    try {
      const response = await createDynamicSubscriptionCheckout({
        agentId: selectedAgentId,
        calls,
        sms,
        facebookInteractions,
        industryId,
        originUrl: window.location.origin,
      });
      const checkoutUrl = response?.data?.checkout_url || response?.data?.url;
      if (!checkoutUrl) {
        throw new Error('Checkout URL not returned');
      }
      window.location.href = checkoutUrl;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to start checkout');
      setCalculatorCheckoutLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handlePurchase = async (itemType, itemId) => {
    if (!isAuthenticated) {
      toast.info('Please sign in to purchase');
      navigate('/login');
      return;
    }

    if ((itemType === 'subscription' || itemType === 'package') && !selectedAgentId) {
      toast.error(
        itemType === 'package'
          ? 'Select an agent before purchasing a minute pack.'
          : 'Select an agent before purchasing a subscription.'
      );
      return;
    }

    setPurchasing(itemId);
    try {
      const response = await createCheckout({
        itemType,
        itemId,
        agentId: itemType === 'subscription' || itemType === 'package' ? selectedAgentId : null,
        originUrl: window.location.origin,
      });
      const checkoutUrl = response?.data?.checkout_url || response?.data?.url;
      if (!checkoutUrl) {
        throw new Error('Checkout URL not returned');
      }
      window.location.href = checkoutUrl;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setPurchasing(null);
    }
  };

  if (loading) {
    if (isBuyMinutes) {
      return <BuyMinutesSkeleton />;
    }
    return (
      <div className="min-h-screen bg-slate-50 p-6 lg:p-8" data-testid="pricing-page-loading">
        <div className="max-w-7xl mx-auto space-y-4">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="min-h-[24rem] w-full rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {isBuyMinutes && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 md:pt-8 pb-2">
          <div className="flex items-start gap-3">
            <Button variant="ghost" size="icon" asChild className="shrink-0 -ml-2 text-slate-600">
              <Link to="/dashboard" aria-label="Back to dashboard">
                <ArrowLeft className="w-5 h-5" />
              </Link>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Buy minute packs</h1>
              <p className="text-slate-600 mt-1 text-sm md:text-base max-w-2xl">
                {hasActiveSubscription
                  ? 'Add prepaid minutes to your account. Packs are valid for 365 days.'
                  : 'Purchase prepaid minute packs, or open the Subscription plans tab to start a plan.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Pricing */}
      <section
        className={isBuyMinutes ? 'py-4 px-4 sm:px-6 lg:px-8 pb-12' : 'py-6 px-4 sm:px-6 lg:px-8 pb-8'}
        data-testid={isBuyMinutes ? 'buy-minutes-page' : 'pricing-page'}
      >
        <div className={isBuyMinutes ? 'max-w-5xl mx-auto' : 'max-w-7xl mx-auto'}>
          {isBuyMinutes && hasActiveSubscription ? (
            <>
              <MinutePackAgentField
                agents={agents}
                selectedAgentId={selectedAgentId}
                onAgentChange={setSelectedAgentId}
                isAuthenticated={isAuthenticated}
              />
              <MinutePackagesSection
                packages={packages}
                purchasing={purchasing}
                onPurchase={handlePurchase}
                purchaseDisabled={!selectedAgentId || agents.length === 0}
                showPerMinuteCost={!isBuyMinutes}
              />
            </>
          ) : !isBuyMinutes ? (
            <>
              <InteractivePricingCalculator
                agents={agents}
                selectedAgentId={selectedAgentId}
                onAgentChange={setSelectedAgentId}
                isAuthenticated={isAuthenticated}
                subscribeSubmitting={calculatorCheckoutLoading}
                onSubscribeNow={handleCalculatorSubscribe}
              />
              {showCustomerBillingPortal ? (
                <div className="max-w-4xl mx-auto mt-8 rounded-xl border border-slate-200 bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">Already subscribed?</p>
                    <p className="text-xs text-slate-500 mt-1">
                      Open Stripe billing for the selected agent to cancel or change billing details.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    disabled={!selectedAgentId || billingPortalLoading}
                    onClick={handleCustomerBillingPortal}
                  >
                    {billingPortalLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : null}
                    Manage or cancel subscription
                  </Button>
                </div>
              ) : null}
            </>
          ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className={`flex justify-center ${isBuyMinutes ? 'mb-8' : 'mb-12'}`}>
              <TabsList className="bg-slate-100 p-1 rounded-full">
                <TabsTrigger 
                  value="plans" 
                  className="rounded-full px-6 py-3 data-[state=active]:bg-white data-[state=active]:shadow-md"
                  data-testid="plans-tab"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Subscription Plans
                </TabsTrigger>
                <TabsTrigger 
                  value="packages" 
                  className="rounded-full px-6 py-3 data-[state=active]:bg-white data-[state=active]:shadow-md"
                  data-testid="packages-tab"
                >
                  <Package className="w-4 h-4 mr-2" />
                  Minute Packs
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Subscription Plans */}
            <TabsContent value="plans">
              <div className="max-w-4xl mx-auto mb-6">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                  <label htmlFor="subscription-agent" className="block text-sm font-medium text-slate-700 mb-2">
                    Select Agent (required for subscription)
                  </label>
                  <select
                    id="subscription-agent"
                    className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm outline-none focus:border-teal-500 focus:ring-2 focus:ring-teal-200"
                    value={selectedAgentId}
                    onChange={(e) => setSelectedAgentId(e.target.value)}
                    disabled={!isAuthenticated || agents.length === 0}
                  >
                    {agents.length === 0 ? (
                      <option value="">
                        {isAuthenticated ? 'No agents available. Create an agent first.' : 'Sign in to load your agents'}
                      </option>
                    ) : (
                      agents.map((agent) => (
                        <option key={agent.agent_id} value={agent.agent_id}>
                          {agent.name || agent.agent_id}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>

              <SubscriptionPlansSection
                plans={plans}
                purchasing={purchasing}
                onPurchase={handlePurchase}
                purchaseDisabled={!selectedAgentId || agents.length === 0}
              />
            </TabsContent>

            {/* Minute Packages */}
            <TabsContent value="packages">
              <MinutePackAgentField
                agents={agents}
                selectedAgentId={selectedAgentId}
                onAgentChange={setSelectedAgentId}
                isAuthenticated={isAuthenticated}
              />
              <MinutePackagesSection
                packages={packages}
                purchasing={purchasing}
                onPurchase={handlePurchase}
                purchaseDisabled={!selectedAgentId || agents.length === 0}
                showPerMinuteCost={!isBuyMinutes}
              />
            </TabsContent>
          </Tabs>
          )}
        </div>
      </section>

      {/* Benefits (pricing page only — no marketing header/footer) */}
      {!isBuyMinutes && (
      <section className="py-10 px-4 sm:px-6 lg:px-8 border-t border-slate-200/80 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">What you get with Desqly</h2>
            <p className="text-slate-600">
              Flexible packaging and usage options so you can match call and message volume to your business — the same
              product capabilities across plans.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Phone, title: 'Australian Number', desc: '1 landline or mobile number' },
              {
                icon: Package,
                title: 'Flexible packaging',
                desc: 'Pick the subscription and add-ons that fit your volume — scale up or down as demand changes.',
              },
              {
                icon: Globe2,
                title: 'Omnichannel',
                desc: 'SMS, WhatsApp & Facebook messaging',
              },
              { icon: Shield, title: 'Call & Message Summaries', desc: 'Transcripts plus AI summaries' },
              { icon: HeartPulse, title: 'Sentiment Analysis', desc: 'Caller mood and intent signals' },
              { icon: Sparkles, title: 'Smart Next Actions', desc: 'Suggested follow-ups and escalations' },
              { icon: ListChecks, title: 'Advanced Analytics', desc: 'Insights, trends, and reporting' },
              { icon: CreditCard, title: 'Spend Monitoring', desc: 'Track your usage in real-time' },
              { icon: Star, title: 'Email Support', desc: 'Dedicated customer support' },
            ].map((feature, i) => (
              <div key={i} className="flex items-start gap-4 p-4 bg-white rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-teal-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold text-slate-900">{feature.title}</h3>
                  <p className="text-sm text-slate-500">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}
    </div>
  );
}

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);
  const [resolvedSessionId, setResolvedSessionId] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();
  const rawSessionId =
    searchParams.get('session_id') ||
    searchParams.get('stripe_session_id') ||
    searchParams.get('checkout_session_id');
  const decodeSafe = (value) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const isSessionPlaceholder = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return true;
    const decoded = decodeSafe(normalized);
    return /^\{.*\}$/.test(decoded) || decoded.includes('CHECKOUT_SESSION_ID');
  };
  const sessionId = isSessionPlaceholder(rawSessionId) ? null : rawSessionId;
  const orderId = searchParams.get('order_id');

  const pollIntervalMs = 2500;
  const maxAttempts = Math.ceil(30000 / pollIntervalMs);

  const renderBadgeClass = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (['paid', 'fulfilled', 'success', 'complete', 'completed'].includes(normalized)) {
      return 'bg-green-100 text-green-700 border-green-200';
    }
    if (['pending', 'processing', 'requires_action'].includes(normalized)) {
      return 'bg-amber-100 text-amber-700 border-amber-200';
    }
    if (['failed', 'expired', 'canceled', 'cancelled'].includes(normalized)) {
      return 'bg-red-100 text-red-700 border-red-200';
    }
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  useEffect(() => {
    let cancelled = false;
    const resolveSession = async () => {
      if (sessionId && !isSessionPlaceholder(sessionId)) {
        setResolvedSessionId(sessionId);
        return;
      }
      if (!orderId) {
        setStatus('invalid');
        return;
      }
      try {
        const response = await getPaymentOrder(orderId);
        const order = response?.data?.order || response?.data || {};
        const derivedSessionId =
          order.session_id ||
          order.checkout_session_id ||
          order.stripe_session_id ||
          null;
        if (!cancelled) {
          if (derivedSessionId) {
            setResolvedSessionId(derivedSessionId);
          } else {
            setStatus('invalid');
          }
        }
      } catch {
        if (!cancelled) {
          setStatus('error');
        }
      }
    };
    resolveSession();
    return () => {
      cancelled = true;
    };
  }, [sessionId, orderId]);

  useEffect(() => {
    if (!resolvedSessionId) return;

    let cancelled = false;
    let timeoutId = null;

    const poll = async (attempt) => {
      if (cancelled) return;
      setAttempts(attempt);

      try {
        const response = await getPaymentStatus(resolvedSessionId);
        const nextData = response?.data || {};
        setPaymentData(nextData);

        const paymentStatus = String(nextData.payment_status || '').toLowerCase();
        const lifecycleStatus = String(nextData.status || '').toLowerCase();

        if (['expired', 'failed', 'canceled', 'cancelled'].includes(lifecycleStatus)) {
          setStatus(lifecycleStatus === 'expired' ? 'expired' : 'error');
          return;
        }

        if (paymentStatus === 'paid') {
          setStatus('success');
          return;
        }
      } catch {
        setStatus('error');
        return;
      }

      if (attempt >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      timeoutId = setTimeout(() => poll(attempt + 1), pollIntervalMs);
    };

    setStatus('checking');
    poll(1);

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [resolvedSessionId, maxAttempts]);

  const handleManualRefresh = async () => {
    if (!resolvedSessionId) return;
    setStatus('checking');
    try {
      const response = await getPaymentStatus(resolvedSessionId);
      const nextData = response?.data || {};
      setPaymentData(nextData);
      const paymentStatus = String(nextData.payment_status || '').toLowerCase();
      if (paymentStatus === 'paid') {
        setStatus('success');
      } else {
        setStatus('timeout');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4" data-testid="payment-success-page">
      <Card className="w-full max-w-md border-0 shadow-2xl text-center overflow-hidden">
        {status === 'checking' && (
          <CardContent className="p-10">
            <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-teal-600 animate-spin" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Processing Payment...</h2>
            <p className="text-slate-500">
              Confirming with our payment service (this step runs fulfillment after Stripe marks the session paid).
            </p>
            <p className="text-xs text-slate-400 mt-2">Attempt {attempts} of {maxAttempts}</p>
          </CardContent>
        )}

        {status === 'success' && (
          <CardContent className="p-10">
            <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
              <Check className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Payment Received</h2>
            <p className="text-slate-500 mb-6">
              Payment is marked as paid. Fulfillment may still be processing.
            </p>
            <div className="space-y-2 text-left mb-6">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Status</span>
                <Badge variant="outline" className={renderBadgeClass(paymentData?.status)}>{paymentData?.status || '-'}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Payment</span>
                <Badge variant="outline" className={renderBadgeClass(paymentData?.payment_status)}>{paymentData?.payment_status || '-'}</Badge>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Fulfillment</span>
                <Badge variant="outline" className={renderBadgeClass(paymentData?.fulfillment_status)}>{paymentData?.fulfillment_status || '-'}</Badge>
              </div>
            </div>
            <div className="space-y-3">
              {orderId && (
                <Button variant="outline" onClick={() => navigate(`/payment/orders/${orderId}`)} className="w-full h-12">
                  View Order Details
                </Button>
              )}
              <Button onClick={() => navigate('/dashboard')} className="w-full h-12 bg-teal-600 hover:bg-teal-700" data-testid="go-to-dashboard-btn">
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </CardContent>
        )}

        {status === 'expired' && (
          <CardContent className="p-10">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              Session Expired
            </h2>
            <p className="text-slate-500 mb-8">
              Your payment session has expired. Please try again.
            </p>
            <Button onClick={() => navigate('/pricing')} className="w-full h-12">
              Try Again
            </Button>
          </CardContent>
        )}

        {(status === 'error' || status === 'timeout') && (
          <CardContent className="p-10">
            <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">
              {status === 'timeout' ? 'Processing Payment' : 'Unable to Verify Payment'}
            </h2>
            <p className="text-slate-500 mb-8">
              {status === 'timeout'
                ? 'Payment is still pending. You can refresh now, or return later.'
                : 'We could not confirm payment status right now. Please refresh or check again shortly.'}
            </p>
            <div className="space-y-3">
              <Button variant="outline" onClick={handleManualRefresh} className="w-full h-12">
                Refresh Status
              </Button>
              <Button variant="outline" onClick={() => navigate('/dashboard')} className="w-full h-12">
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        )}

        {status === 'invalid' && (
          <CardContent className="p-10">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">Missing Payment Session</h2>
            <p className="text-slate-500 mb-8">
              We could not locate a valid Stripe Checkout session ID from this URL. The success page needs{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">session_id</code> (or{' '}
              <code className="text-xs bg-slate-100 px-1 rounded">stripe_session_id</code>) so we can call payment
              status and complete fulfillment. If you only have an order id, open the link from your email or payment
              history.
            </p>
            <Button onClick={() => navigate('/buy-minutes')} className="w-full h-12">
              Back to Purchases
            </Button>
          </CardContent>
        )}
      </Card>
    </div>
  );
}

function BuyMinutesSkeleton() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 pt-8 space-y-6">
        <div className="flex gap-3">
          <Skeleton className="h-10 w-10 rounded-md" />
          <div className="space-y-2 flex-1">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-full max-w-md" />
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="border-0 shadow-md">
              <CardContent className="p-8 space-y-4">
                <Skeleton className="h-20 w-20 rounded-2xl mx-auto" />
                <Skeleton className="h-8 w-32 mx-auto" />
                <Skeleton className="h-10 w-24 mx-auto" />
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}

function PricingSkeleton() {
  return (
    <div className="min-h-screen bg-white">
      <div className="pt-32 pb-20 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-4">
          <Skeleton className="h-8 w-48 mx-auto" />
          <Skeleton className="h-16 w-96 mx-auto" />
          <Skeleton className="h-6 w-80 mx-auto" />
        </div>
      </div>
      <div className="py-16 px-4">
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {[1, 2].map((i) => (
            <Card key={i} className="border-0 shadow-lg">
              <CardContent className="p-8 space-y-4">
                <Skeleton className="h-8 w-32" />
                <Skeleton className="h-12 w-40" />
                <Skeleton className="h-16 w-full" />
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((j) => (
                    <Skeleton key={j} className="h-5 w-full" />
                  ))}
                </div>
                <Skeleton className="h-12 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
